import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  find as _find,
  get as _get,
  map as _map,
  set as _set,
} from 'lodash';

import { localeMap } from 'utils/currencyFormat';

import StructuredData from '../StructuredData';

const tryAppendField = (productSchema, fieldName, value) => {
  if (value) {
    return _set({ ...productSchema }, fieldName, value);
  }

  return productSchema;
};

const tryAppendRating = (productSchema, rating) => {
  if (rating.totalCount > 0) {
    return {
      ...productSchema,
      aggregateRating: {
        "@type": `AggregateRating`,
        bestRating: `5`,
        ratingValue: `${rating.avgRating}`,
        reviewCount: `${rating.totalCount}`,
      },
    };
  }

  return productSchema;
};

const formatVariantInput = (variants, customVariantData) => {
  return _map(variants, (variant) => {
    const { attributes } = variant;
    const customVariant = _find(customVariantData, (custom) => custom.sku && attributes.sku && custom.sku.toUpperCase() === attributes.sku.toUpperCase()) || {};

    return {
      name: attributes.name,
      price: attributes.price,
      sku: attributes.sku,
      ...customVariant,
    };
  });
};

const formatVariantsToProductSchema = (products, rating, locale, desc, image) => {
  const priceCurrency = localeMap[locale].code;

  return _map(products, (product) => {
    let productSchema = {
      "@type": `Product`,
      name: product.name,
      sku: product.sku,
      itemCondition: `http://schema.org/NewCondition`,
      offers: {
        "@type": `Offer`,
        availability: `http://schema.org/InStock`,
        price: `${product.price}`,
        priceCurrency,
      },
    };

    productSchema = tryAppendRating(productSchema, rating);
    productSchema = tryAppendField(productSchema, `description`, desc);
    productSchema = tryAppendField(productSchema, `gtin12`, product.gtin);
    productSchema = tryAppendField(productSchema, `image`, product.image || image);

    return productSchema;
  });
};

const StructuredProductData = ({customVariantData, desc, graph, image, locale, rating, variants}) => {
  if (!variants && !graph) {
    return null;
  }

  const formattedProducts = graph || formatVariantInput(variants, customVariantData);

  return <StructuredData graph={formatVariantsToProductSchema(formattedProducts, rating, locale, desc, image)} />;
};

StructuredProductData.defaultPropTypes = {
  customVariantData: [],
};

StructuredProductData.propTypes = {
  customVariantData: PropTypes.arrayOf(PropTypes.shape({
    gtin: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    image: PropTypes.string,
    name: PropTypes.string,
    sku: PropTypes.string.isRequired,
  })),
  desc: PropTypes.string,
  graph: PropTypes.arrayOf(PropTypes.shape({
    gtin: PropTypes.number,
    name: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    sku: PropTypes.string.isRequired,
  })),
  image: PropTypes.string,
  locale: PropTypes.string.isRequired,
  rating: PropTypes.shape({
    avgRating: PropTypes.number,
    totalCount: PropTypes.number.isRequired,
  }).isRequired,
  variants: PropTypes.arrayOf(PropTypes.object),
};

const mapStateToProps = (state, ownProps) => {
  // If the graph prop is passed in from the parent, we'll use that instead of variant data.
  const variantProps = !ownProps.graph ? {
    variants: ownProps.variants || _get(state, `product.variants`),
  } : {};

  return {
    locale: _get(state, `i18n.locale`),
    rating: ownProps.rating || {
      avgRating: _get(state, `product.product.ratings-summary.avg-rating`),
      totalCount: _get(state, `product.product.ratings-summary.total-count`, 0),
    },
    ...variantProps,
  };
};

export default connect(mapStateToProps)(StructuredProductData);
